import * as React from 'react';
import { translate } from 'react-i18next';
import * as c from 'models/constants';
import { FieldArrayFieldsProps, ICropImageData, InjectedFormProps, ITranslateProps } from 'models/common';
import { Field, FieldArray } from 'redux-form';
import { FormImages, PortalInputField, PortalSelectMenu, PortalSwitchField } from 'components/FormControls';
import { connect, Dispatch } from 'react-redux';
import { IStore } from 'redux/IStore';
import { IProduct, IProductForm, IProductVariation } from 'models/product/product';
import { attributesDefinitions, attributesToShow, contentUnit } from 'helpers/products';
import { createDomainValueOptions } from 'helpers/common';
import { EllipseBlackWhiteButton } from 'components/Common/Button';
import { required } from 'helpers/validationattributes';
import { closeModal, showModal } from 'redux/modules/modal';
import { clearProductFormError, cropAndSaveProductImage, requestFailure } from 'redux/modules/product/manageproduct';
import renderIcon from 'helpers/renderIcon';

interface IOwnProps extends InjectedFormProps<IProductForm> {
	currentFormValues: IProductForm;
}
interface IProps
	extends IOwnProps,
		ReturnType<typeof mapStateToProps>,
		ReturnType<typeof mapDispatchToProps>,
		ITranslateProps {}

@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class ProductVariationsForm extends React.Component<IProps, {}> {
	constructor(props) {
		super(props);

		this.imageUploadFailed = this.imageUploadFailed.bind(this);
		this.imageUploadSuccess = this.imageUploadSuccess.bind(this);
		this.renderVariation = this.renderVariation.bind(this);
		this.renderVariations = this.renderVariations.bind(this);
	}

	private imageUploadSuccess() {
		this.props.clearProductFormError();
		this.forceUpdate();
	}

	private imageUploadFailed(error: any) {
		if (error) {
			this.props.requestFailure(error);
			this.forceUpdate();
		}
	}

	private createEmptyVariation(): IProductVariation {
		const { variations } = this.props.currentFormValues;
		let lastExisting: IProductVariation = {};
		if (variations?.length) {
			lastExisting = variations[variations.length - 1] || {};
		}
		const dontCopy: Array<keyof IProductVariation> = ['id', 'sku', 'unitsInStock'];
		const item = {};
		for (const key in lastExisting) {
			if (dontCopy.includes(key as any)) {
				continue;
			}
			let value = lastExisting[key];
			if (value && typeof value === 'object') {
				value = JSON.parse(JSON.stringify(value));
			}
			item[key] = value;
		}
		return item;
	}

	private renderVariation(
		fieldPrefix: string,
		index: number,
		fields: FieldArrayFieldsProps<IProductVariation>,
	): JSX.Element {
		const { t, attributes, currentFormValues, change } = this.props;
		if (index && !currentFormValues.hasVariations) {
			return null;
		}
		const standardFields = this.getStandardFields();
		const rowItems: JSX.Element[] = [];
		if (currentFormValues.hasVariations) {
			rowItems.push(
				<div key="ident">
					<span>{index + 1}</span>
				</div>,
			);
		}
		for (const attr of attributes) {
			const definition = attributesDefinitions[attr];
			if (!definition) {
				throw new Error('Definition for attribute ' + attr + ' is missing!');
			}
			if (definition.domainValue) {
				if (!(definition.domainValue in this.props)) {
					throw new Error(definition.domainValue + ' not in props');
				}
				rowItems.push(
					<Field
						key={attr}
						name={fieldPrefix + '.attributes.' + attr}
						menuPosition="fixed"
						component={PortalSelectMenu}
						items={createDomainValueOptions(t, definition.prefix, this.props[definition.domainValue])}
						label={t(definition.label)}
					/>,
				);
			} else {
				rowItems.push(
					<Field
						key={attr}
						name={fieldPrefix + '.attributes.' + attr}
						component={PortalInputField}
						type="text"
						label={t(definition.label)}
					/>,
				);
			}
		}
		const buildField = (key: keyof IProductForm): JSX.Element => {
			const name = fieldPrefix + '.' + key;
			const requiredFields: Array<keyof IProductForm> = ['price', 'sku'];
			if (key === 'images') {
				return (
					<div key={key}>
						<FieldArray
							name={name}
							component={FormImages}
							images={currentFormValues?.variations?.[index]?.images || []}
							maxImageSize={c.MAX_PRODUCT_IMAGE_SIZE}
							imageUploadSuccess={this.imageUploadSuccess}
							imageUploadFailed={this.imageUploadFailed}
							enableSelectThumbnail={true}
							enableSelectImage={true}
							cropImage={true}
							cropImageOptions={{
								...c.DEFAULT_CROP_OPTIONS,
								aspectRatio: 515 / 667,
							}}
							label={t('Shop_ManageShop_Products_CreateProduct_Label_Images')}
							imageDescription={t('Shop_ManageShop_Products_CreateProduct_ImageUpload_Text_ImageDescription')}
							uploadDescription={t('Shop_ManageShop_Products_CreateProduct_ImageUpload_Text_UploadDescription')}
							change={change}
							showModal={this.props.showModal}
							closeModal={this.props.closeModal}
						/>
					</div>
				);
			}
			const inputProps: { [key: string]: any } = {
				key,
				name,
				component: PortalInputField,
				validate: requiredFields.includes(key) ? [required] : undefined,
			};
			switch (key) {
				case 'content':
				case 'price':
				case 'discountedPrice':
				case 'unitsInStock':
					inputProps.type = 'number';
					inputProps.className = 'basicNumber';
					break;
				default:
					inputProps.type = 'text';
					inputProps.className = 'basicText';
			}
			return <Field {...inputProps} />;
		};
		for (const key in standardFields) {
			if (standardFields[key]) {
				rowItems.push(buildField(key as any));
			}
		}
		rowItems.push(
			<div key="delete">
				{index ? (
					<button className="btnIcon round" onClick={() => fields.remove(index)}>
						{renderIcon('delete_2')}
					</button>
				) : null}
			</div>,
		);
		return <React.Fragment key={index}>{rowItems}</React.Fragment>;
	}

	private get categoryKey(): IProduct['attributes']['CategoryKey'] {
		const { currentFormValues } = this.props;
		if (currentFormValues.thirdDropdownCategoryKey) {
			return currentFormValues.thirdDropdownCategoryKey;
		}
		if (currentFormValues.secondDropdownCategoryKey) {
			return currentFormValues.secondDropdownCategoryKey;
		}
		if (currentFormValues.firstDropdownCategoryKey) {
			return currentFormValues.firstDropdownCategoryKey;
		}
	}

	private getStandardFields(): Partial<{ [key in keyof IProductForm]: React.ReactNode }> {
		const { t } = this.props;
		const unit = contentUnit(this.categoryKey);
		return {
			content: unit ? t('Shop_ManageShop_Products_CreateProduct_Label_Content') + ' (' + unit + ')' : null,
			images: t('Shop_ManageShop_Products_CreateProduct_Label_Images'),
			price: t('Shop_ManageShop_Products_CreateProduct_Label_Price'),
			discountedPrice: t('Shop_ManageShop_Products_CreateProduct_Label_SalePrice'),
			unitsInStock: t('Shop_ManageShop_Products_CreateProduct_Label_Quantity'),
			sku: t('Shop_ManageShop_Products_CreateProduct_Label_SKU'),
		};
	}

	private renderVariations({ fields }: { fields: FieldArrayFieldsProps<IProductVariation> }): JSX.Element {
		const { t, attributes, currentFormValues } = this.props;
		const hasVariations = currentFormValues.hasVariations;
		const headerItems: JSX.Element[] = [];
		const addItem = (key: string, content: React.ReactNode, className = '') => {
			headerItems.push(
				<div
					key={key}
					className={'table-header-item editable ' + className}
					style={{ gridColumnStart: headerItems.length + 1 }}
				>
					{content}
				</div>,
			);
		};
		if (hasVariations) {
			addItem('index', '#');
		}
		for (const attr of attributes) {
			const definition = attributesDefinitions[attr];
			if (!definition) {
				throw new Error('Definition for attribute ' + attr + ' is missing!');
			}
			addItem(attr, t(definition.label));
		}
		const standardFields = this.getStandardFields();
		for (const name in standardFields) {
			if (standardFields[name]) {
				addItem(name, standardFields[name]);
			}
		}
		addItem('delete', '', 'delete');
		return (
			<>
				<div className="productVariationsTable">
					{headerItems}
					{fields.map(this.renderVariation)}
				</div>
				{hasVariations ? (
					<EllipseBlackWhiteButton onClick={() => fields.push(this.createEmptyVariation())}>
						{t('Shop_ManageShop_Products_CreateProduct_Button_AddVariation')}
					</EllipseBlackWhiteButton>
				) : null}
			</>
		);
	}

	public render() {
		const { t } = this.props;

		return (
			<div className="productVariationsWrapper">
				<h1 className="text-center d-none d-lg-block">{t('Shop_ManageShop_Products_Inventory_Title')}</h1>
				<Field
					name="hasVariations"
					component={PortalSwitchField}
					label={t('Shop_ManageShop_Products_Variations_Enabled')}
				/>
				<FieldArray name="variations" component={this.renderVariations} />
			</div>
		);
	}
}

const mapStateToProps = (state: IStore, ownProps: IOwnProps) => ({
	colors: state.domainValues.colors,
	flavours: state.domainValues.flavours,
	attributes: attributesToShow(ownProps.currentFormValues, 'variations'),
});

const mapDispatchToProps = (dispatch: Dispatch<IStore>) => ({
	closeModal: () => dispatch(closeModal()),
	showModal: (modalType: string, modalProps: any) => dispatch(showModal(modalType, modalProps)),
	requestFailure: (error) => dispatch(requestFailure(error)),
	clearProductFormError: () => dispatch(clearProductFormError()),
	cropAndSaveProductImage: (image: ICropImageData, index: number, fieldName: string, formName: string) =>
		dispatch(cropAndSaveProductImage(image, index, fieldName, formName)),
});

const ProductVariations = connect(mapStateToProps, mapDispatchToProps)(ProductVariationsForm);

export { ProductVariations };
